import dynamic from 'next/dynamic'
import { usePage } from '../../contexts'
import { IPage, IVideo } from '../../types'
import { isVerticalVideo } from '../../utils'
import {
  isArticlePage,
  isArticleTimeLinePage,
  isRecipePage,
} from '../../types/checkers'
import { VerticalArticleVideo } from '../ArticleVideo/VerticalArticleVideo'
import BigBrotherThumbnail from '../BigBrother/Thumbnail'
import { Player } from '../Player/Player'
import BigBrotherVideoRegister from '../TimeLine/BigBrother/BigBrotherVideoRegister'
import Thumbnail from '../TimeLine/BigBrother/Thumbnail'

const TimeLine = dynamic(() => import('../TimeLine/TimeLine'), { ssr: true })

type BBProps = {
  anchorId: string
  index: number
  isTimeLine: boolean
  isBigBrother: boolean
  title: string
  time: string
}

type Props = {
  type: ShortCodeTypes
  shortcode: string
  keyPrefix: string
  bbProps?: BBProps
}

type ShortCodeTypes = 'video' | 'timeline' | 'image'

const regexTypes = {
  video:
    '\\[brightcove_iframe(\\s[\\s\\S]*?)?\\](?:((?!\\s*?(?:\\[brightcove_iframe|\\[/(?!brightcove_iframe)))[\\s\\S]*?)(\\[/brightcove_iframe\\]))?',
  timeline:
    '\\[timeline(\\s[\\s\\S]*?)?\\](?:((?!\\s*?(?:\\[timeline|\\[/(?!timeline)))[\\s\\S]*?)(\\[/timeline\\]))?',
  image:
    '\\[img_figure(\\s[\\s\\S]*?)?\\](?:((?!\\s*?(?:\\[img_figure|\\[/(?!img_figure)))[\\s\\S]*?)(\\[/img_figure\\]))?',
}

//parse
const parseOptions = (stringOptions: string) => {
  const options: { [key: string]: string } = {}
  if (!stringOptions) {
    return
  }
  const set = stringOptions.replace(/(\w+=)/g, '\n$1').split('\n')
  set.shift()
  for (let i = 0; i < set.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const kv: any = set[i].split('=')
    kv[1] = (kv[1] || '').trim()
    options[kv[0]] = kv[1].replace(/(^['"])|(['"]$)/g, '')
  }
  return options
}

const Parser = ({ type, shortcode, bbProps, keyPrefix }: Props) => {
  const pageData = usePage()

  if (!pageData) {
    return null
  }

  const stringOptions = shortcode.match(new RegExp(regexTypes[type]))

  if (!stringOptions) {
    return null
  }

  const options = parseOptions(stringOptions[1])
  if (!options) {
    return null
  }

  /**
   * RESH13-1138 - the exact condition under which <Article> is rendered, so
   * that the vertical branch below cannot leak into recipes, timeline items,
   * the facelift article or the war timeline. Narrowed through a separate
   * alias, otherwise the guards would narrow pageData itself, which the rest
   * of this component still uses as a plain IPage.
   */
  const page: IPage = pageData
  const isItemPage =
    isArticlePage(page) && !isRecipePage(page) && !isArticleTimeLinePage(page)

  const { video_id, credit, desc, poster, kid, duration = '' } = options
  const videoProps = {
    duration: duration ? parseInt(duration.replace(/'/g, '')) : 0,
    poster: poster,
    kalturaId: kid,
    name: video_id,
    credit: credit?.replace(/\\['"′]/g, match => {
      if (match.endsWith('′') || match.endsWith("'")) {
        return "'"
      }
      return '"'
    }),
    description: desc?.replace(/\\['"′]/g, match => {
      if (match.endsWith('′') || match.endsWith("'")) {
        return "'"
      }
      return '"'
    }),
    autoPlay: false,
  } as IVideo

  switch (type) {
    case 'video':
      if (keyPrefix.includes('big_brother')) {
        return <BigBrotherThumbnail video={videoProps} bbProps={bbProps} />
      }
      if (bbProps?.isBigBrother && bbProps?.isTimeLine) {
        return (
          <BigBrotherVideoRegister video={videoProps} bbProps={bbProps}>
            <Thumbnail video={videoProps} bbProps={bbProps} />
          </BigBrotherVideoRegister>
        )
      }
      /**
       * RESH13-1138 - a vertical video in the body behaves like a vertical main
       * video: a 9/16 poster that opens the vertical player fullscreen.
       * Limited to the item page - the same parser also runs for recipes,
       * timeline items and the war timeline, which are out of scope.
       * The shortcode also carries an `aspect` attribute, but it is not usable
       * as the marker: qdata caps the Kaltura height at 1 before computing it
       * (VideoItem_kaltura), so it does not describe the real orientation.
       */
      if (isItemPage && isVerticalVideo(videoProps)) {
        return (
          <VerticalArticleVideo
            pageMeta={pageData.PageMeta}
            video={videoProps}
            fromParser={true}
            key={kid}
          />
        )
      }
      return (
        <Player
          pageMeta={pageData.PageMeta}
          video={videoProps}
          img={''}
          fromParser={true}
          key={kid}
        />
      )
    case 'timeline':
      return <TimeLine data={pageData} key={'tl_id_' + options.id} />

    // case "image":
    //     return <ImageFigure image={options} />
    default:
      return null
  }
}

export const isShortCode = (shortcode: string, key = '', bbProps?: BBProps) => {
  let shortcodeType = ''
  if (shortcode.match(new RegExp(regexTypes['video']))) {
    shortcodeType = 'video'
  }
  if (shortcode.match(new RegExp(regexTypes['timeline']))) {
    shortcodeType = 'timeline'
  }
  if (shortcode.match(new RegExp(regexTypes['image']))) {
    shortcodeType = 'image'
  }

  if (shortcodeType) {
    return (
      <Parser
        type={shortcodeType as ShortCodeTypes}
        shortcode={shortcode}
        key={'parser' + key}
        keyPrefix={key}
        bbProps={bbProps}
      />
    )
  } else {
    return null
  }
}
